import { Box, Flex, Image, Text, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { Carousel } from 'components';
import { Stack, DownloadSimple, MapPin } from 'components/Icons/Icons';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadImages, downloadPDF } from 'modules/projects/projectsSlice';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { mq } from 'styles/breakpoints';
import { rem } from 'utils/fonts';
import { getCountryWithCity } from 'utils/project';
import useBreakpoint from 'hooks/useBreakpoint';
import ProjectInfo from '../Pdf/ProjectInfo';
import { Button } from 'components';

//region styled

const TagWrapper = styled(Flex)`
  width: 100%;
`;

const Title = styled(Text)`
  font-size: ${rem('24px')};
  margin-bottom: 5px;

  ${mq.lg} {
    font-size: ${rem('28px')};
  }
`;
const ProjectSubtitle = styled(Text)`
  margin-bottom: 15px;
`;

const StyledLabel = styled.span`
  margin-left: 10px;
`;

const StyledImage = styled(Box)<{ url?: string; carousel?: boolean }>`
  background-image: url(${(props): string | undefined => props.url});
  background-size: cover;
  height: 185px;
  border-radius: 6px;
  margin-bottom: ${(props): string => (props.carousel ? '70px' : '35px')};
  position: relative;
  width: 100%;

  ${mq.lg} {
    margin-bottom: ${(props): string => (props.carousel ? '70px' : '35px')};
    .slick-dots {
      bottom: -45px;
    }
  }
`;

//endregion

const ProjectHeader: FC<{ project?: Project }> = ({ project }) => {
  const { t } = useTranslation('projects');
  const theme = useTheme();
  const { fetchingProjectImages, generatingPDF } = useSelector((state: RootState) => state.projects);
  const dispatch = useDispatch();
  const { isMobile } = useBreakpoint();
  const { colors } = theme;

  const downloadImagesFromProject = (): void => {
    const urls = (project?.additionalImages ?? []).filter(image => image.url).map(image => `${image.url}?fm=png`);
    project?.imageUrl && urls.push(`${project?.imageUrl}?fm=png`);
    dispatch(downloadImages(urls, project?.slug ? `${project?.slug}.zip` : 'project.zip'));
  };

  const generatePdfDocument = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    dispatch(downloadPDF([<ProjectInfo key="projectInfo" theme={theme} project={project} />], project?.title ?? 'project'));
  };

  return (
    <Flex flexDirection="column" alignItems="center">
      <Title as="h1" fontFamily="heading" color="secondary.800">
        {project?.title}
      </Title>
      <ProjectSubtitle fontSize="md" color="secondary.600">
        {getCountryWithCity(project)}
      </ProjectSubtitle>
      <StyledImage url={project?.imageUrl ? `${project?.imageUrl}?q=100&h=294&auto=format` : ''} carousel={!!project?.additionalImages?.length}>
        <Carousel color={colors.primary[500]}>
          <Image
            src={project?.imageUrl ? `${project?.imageUrl}?q=100&h=194&auto=format` : ''}
            alt=""
            h="194px"
            borderRadius="6px"
            objectFit="cover"
          />
          {project?.additionalImages?.map(img => (
            <Image src={`${img.url}?q=90&h=294&auto=format`} alt={img.alt} key={img.url} h="194px" borderRadius="6px" objectFit="cover" />
          ))}
        </Carousel>
      </StyledImage>
      <TagWrapper justifyContent={['center', null, null, 'space-between', 'center']} flexWrap="wrap">
        <Flex sx={{ button: { margin: '0 5px 10px 0' } }} w={isMobile ? '100%' : 'auto'} justifyContent="center">
          {project?.factUrl && (
            <a href={project?.factUrl} target="_blank" rel="noopener noreferrer">
              <Button padding="0px 15px" variant="secondary" fontSize="xs" height="40px" minWidth={isMobile ? '160px' : '80px'}>
                {t('facts')}
              </Button>
            </a>
          )}
          <a
            //URL based on https://stackoverflow.com/questions/47038116/google-maps-url-with-pushpin-and-satellite-basemap
            href={`https://www.google.com/maps?t=k&q=loc:${project?.location.lat}+${project?.location.lng}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button padding="0px 15px" variant="secondary" fontSize="xs" minWidth={isMobile ? '150px' : '120px'} height="40px" rightIcon={<MapPin />}>
              {t('map')}
            </Button>
          </a>
        </Flex>
        <Flex sx={{ button: { margin: '0 5px 10px 0' } }} w={isMobile ? '100%' : 'auto'} justifyContent="center">
          <Button
            padding="0px 15px"
            variant="outline"
            fontSize="xs"
            minWidth={isMobile ? '150px' : '100px'}
            maxW={isMobile ? '150px' : 'auto'}
            height="40px"
            leftIcon={<DownloadSimple />}
            isLoading={generatingPDF}
            onClick={generatePdfDocument}
          >
            <StyledLabel>{t('downloadPDF')}</StyledLabel>
          </Button>
          <Button
            padding="0px 15px"
            variant="outline"
            fontSize="xs"
            minWidth={isMobile ? '150px' : '100px'}
            maxW={isMobile ? '150px' : 'auto'}
            height="40px"
            leftIcon={<Stack />}
            onClick={downloadImagesFromProject}
            isLoading={fetchingProjectImages}
          >
            <StyledLabel>{t('downloadImages')}</StyledLabel>
          </Button>
        </Flex>
      </TagWrapper>
    </Flex>
  );
};

export default ProjectHeader;
